import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { useNavigate } from "react-router-dom"
import {
  ColorRank,
  DeckChallengeReceivedPayload,
  DeckChallengeSentPayload,
  DeckSearchMode,
} from "@/types/battle"
import { DECK_BATTLE_ENABLED } from "@/constants/guardians"
import { useAuth } from "@/context/AuthContext"
import {
  connectBattleSocket,
  consumePendingDeckBattleStarted,
  emitBattleEventWhenReady,
  onBattleEvent,
} from "@/socket/battleSocket"
import {
  normalizeDeckBattleStartedPayload,
  normalizeDeckChallengeReceivedPayload,
  normalizeDeckChallengeSentPayload,
  normalizeDeckChallengeStatusPayload,
} from "@/utils/deckPayload"
import { buildDeckChallengeSendDto } from "@/utils/deckSearch"
import IncomingDeckChallengeModal from "@/features/battle/IncomingDeckChallengeModal"

const PRESENCE_ERROR_MSG =
  "Арена ещё подключается. Подождите пару секунд и попробуйте снова."

type DeckChallengeContextValue = {
  incomingDeckChallenge: DeckChallengeReceivedPayload | null
  outgoingDeckChallenge: DeckChallengeSentPayload | null
  sendDeckChallenge: (
    targetUserId: number,
    deckSearchMode: DeckSearchMode,
    cardIds: string[],
    colorRank?: ColorRank
  ) => void
  markDeckAcceptPending: () => void
  declineIncomingDeck: () => void
  cancelOutgoingDeck: () => void
  clearOutgoingDeck: () => void
}

const DeckChallengeContext = createContext<DeckChallengeContextValue>({
  incomingDeckChallenge: null,
  outgoingDeckChallenge: null,
  sendDeckChallenge: () => {},
  markDeckAcceptPending: () => {},
  declineIncomingDeck: () => {},
  cancelOutgoingDeck: () => {},
  clearOutgoingDeck: () => {},
})

export function useBattleDeckChallenge() {
  return useContext(DeckChallengeContext)
}

const noopValue: DeckChallengeContextValue = {
  incomingDeckChallenge: null,
  outgoingDeckChallenge: null,
  sendDeckChallenge: () => {},
  markDeckAcceptPending: () => {},
  declineIncomingDeck: () => {},
  cancelOutgoingDeck: () => {},
  clearOutgoingDeck: () => {},
}

export function DeckChallengeProvider({ children }: { children: ReactNode }) {
  if (!DECK_BATTLE_ENABLED) {
    return (
      <DeckChallengeContext.Provider value={noopValue}>
        {children}
      </DeckChallengeContext.Provider>
    )
  }
  return <DeckChallengeProviderInner>{children}</DeckChallengeProviderInner>
}

function DeckChallengeProviderInner({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const { token, isAuthenticated } = useAuth()
  const [incomingDeckChallenge, setIncomingDeckChallenge] =
    useState<DeckChallengeReceivedPayload | null>(null)
  const [outgoingDeckChallenge, setOutgoingDeckChallenge] =
    useState<DeckChallengeSentPayload | null>(null)

  const navigatingRef = useRef(false)
  const awaitingBattleRef = useRef(false)
  const sendingRef = useRef(false)
  const pendingDeckAcceptRef = useRef(false)

  const goDeckDuel = useCallback(
    (raw: unknown) => {
      const battle = normalizeDeckBattleStartedPayload(raw)
      if (!battle || navigatingRef.current) return
      navigatingRef.current = true
      awaitingBattleRef.current = false
      sendingRef.current = false
      pendingDeckAcceptRef.current = false
      setIncomingDeckChallenge(null)
      setOutgoingDeckChallenge(null)
      navigate("/play/deck-duel", { state: { battle } })
      setTimeout(() => {
        navigatingRef.current = false
      }, 500)
    },
    [navigate]
  )

  const markDeckAcceptPending = useCallback(() => {
    pendingDeckAcceptRef.current = true
    awaitingBattleRef.current = true
  }, [])

  const sendDeckChallenge = useCallback(
    (
      targetUserId: number,
      deckSearchMode: DeckSearchMode,
      cardIds: string[],
      colorRank?: ColorRank
    ) => {
      if (sendingRef.current || awaitingBattleRef.current) return
      sendingRef.current = true
      const payload = buildDeckChallengeSendDto(
        targetUserId,
        deckSearchMode,
        cardIds,
        colorRank
      )
      void emitBattleEventWhenReady(token, "deck-challenge-send", payload).then((ok) => {
        if (!ok) {
          sendingRef.current = false
          window.alert(PRESENCE_ERROR_MSG)
        }
      })
    },
    [token]
  )

  const declineIncomingDeck = useCallback(() => {
    const challenge = incomingDeckChallenge
    if (!challenge) return
    setIncomingDeckChallenge(null)
    pendingDeckAcceptRef.current = false
    void emitBattleEventWhenReady(token, "deck-challenge-decline", {
      challengeId: challenge.challengeId,
    })
  }, [incomingDeckChallenge, token])

  const cancelOutgoingDeck = useCallback(() => {
    const challenge = outgoingDeckChallenge
    if (!challenge) return
    setOutgoingDeckChallenge(null)
    awaitingBattleRef.current = false
    sendingRef.current = false
    void emitBattleEventWhenReady(token, "deck-challenge-cancel", {
      challengeId: challenge.challengeId,
    })
  }, [outgoingDeckChallenge, token])

  const clearOutgoingDeck = useCallback(() => {
    setOutgoingDeckChallenge(null)
    awaitingBattleRef.current = false
    sendingRef.current = false
  }, [])

  useEffect(() => {
    if (!isAuthenticated || !token) return

    const cleanups = [
      onBattleEvent("deck-challenge-sent", (payload) => {
        const normalized = normalizeDeckChallengeSentPayload(payload)
        if (!normalized) return
        sendingRef.current = false
        awaitingBattleRef.current = true
        setOutgoingDeckChallenge(normalized)
        navigate("/play/deck-challenge-waiting")
      }),
      onBattleEvent("deck-challenge-received", (payload) => {
        const normalized = normalizeDeckChallengeReceivedPayload(payload)
        if (normalized) setIncomingDeckChallenge(normalized)
      }),
      onBattleEvent("deck-challenge-declined", (payload) => {
        const status = normalizeDeckChallengeStatusPayload(payload)
        if (!status) return
        setOutgoingDeckChallenge((prev) => {
          if (prev?.challengeId !== status.challengeId) return prev
          awaitingBattleRef.current = false
          window.alert(status.message || "Вызов отклонён")
          return null
        })
      }),
      onBattleEvent("deck-challenge-cancelled", (payload) => {
        const status = normalizeDeckChallengeStatusPayload(payload)
        if (!status) return
        setIncomingDeckChallenge((prev) =>
          prev?.challengeId === status.challengeId ? null : prev
        )
        setOutgoingDeckChallenge((prev) =>
          prev?.challengeId === status.challengeId ? null : prev
        )
        awaitingBattleRef.current = false
        pendingDeckAcceptRef.current = false
      }),
      onBattleEvent("deck-battle-started", (payload) => {
        const inChallengeFlow =
          awaitingBattleRef.current || pendingDeckAcceptRef.current
        if (!inChallengeFlow) return
        goDeckDuel(payload)
      }),
      onBattleEvent("error", (p) => {
        sendingRef.current = false
        if (awaitingBattleRef.current || pendingDeckAcceptRef.current) {
          awaitingBattleRef.current = false
          pendingDeckAcceptRef.current = false
          setOutgoingDeckChallenge(null)
        }
        window.alert(p.message)
      }),
    ]

    void connectBattleSocket(token)
      .then(() => {
        const pending = consumePendingDeckBattleStarted()
        const inChallengeFlow =
          awaitingBattleRef.current || pendingDeckAcceptRef.current
        if (pending && inChallengeFlow) {
          goDeckDuel(pending)
        }
      })
      .catch(() => null)

    return () => {
      cleanups.forEach((fn) => fn())
    }
  }, [token, isAuthenticated, goDeckDuel, navigate])

  const value = useMemo(
    () => ({
      incomingDeckChallenge,
      outgoingDeckChallenge,
      sendDeckChallenge,
      declineIncomingDeck,
      cancelOutgoingDeck,
      clearOutgoingDeck,
      markDeckAcceptPending,
    }),
    [
      incomingDeckChallenge,
      outgoingDeckChallenge,
      sendDeckChallenge,
      declineIncomingDeck,
      cancelOutgoingDeck,
      clearOutgoingDeck,
      markDeckAcceptPending,
    ]
  )

  return (
    <DeckChallengeContext.Provider value={value}>
      {children}
      <IncomingDeckChallengeModal />
    </DeckChallengeContext.Provider>
  )
}
